import { AdjustChip, AdjustChipGroup, AdjustGroupHeader, AdjustIconAction, AdjustIconActionGroup, AdjustReadout, AdjustSlider, AdjustTextButton } from '../AdjustControlPrimitives.component'
import { CROP_ASPECTS, DEFAULT_CROP, STRAIGHTEN_LIMIT } from '../adjust.types'
import { FC, useMemo } from 'react'
import { frameRectOf, refitRect, totalRotation } from '../adjustGeometry'

import CropLandscapeOutlinedIcon from '@mui/icons-material/CropLandscapeOutlined'
import CropPortraitOutlinedIcon from '@mui/icons-material/CropPortraitOutlined'
import FlipIcon from '@mui/icons-material/Flip'
import { LabSegmented } from '../../lab/LabPanelPrimitives.component'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import { Stack } from '@mui/material'
import { useAdjustEditor } from '../AdjustEditor.context'
import { useTranslation } from 'react-i18next'

const NAMED_RATIOS = new Set(['custom', 'original', 'square'])

/** '16:9' reads '9:16' while Portrait is on */
const ratioLabel = (id: string, isPortrait: boolean) => isPortrait ? id.split(':').reverse().join(':') : id

/** Today's ratios, straighten (the frame shrinks so the corners stay filled), quarter turns and flips */
export const CropControls: FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { doc, base, apply, commit } = useAdjustEditor()
  const crop = doc.crop
  const isOrientable = !NAMED_RATIOS.has(crop.aspect)

  const frame = useMemo(() => base ? frameRectOf(crop, base.size) : null, [base, crop])
  const isChanged = JSON.stringify(crop) !== JSON.stringify(DEFAULT_CROP)

  /** A rotation change keeps a dragged frame when it still fits (shrunk about its centre), else the largest one */
  const rotate = (patch: { straighten?: number, quarterTurns?: number }, historyKey?: string) => {
    const next = { ...crop, ...patch }
    const rect = base ? refitRect(crop.rect, totalRotation(next), base.size) : null
    apply({ type: 'setCrop', crop: { ...patch, rect } }, historyKey)
  }

  return (
    <Stack gap="1.2rem">

      <AdjustGroupHeader title={t('adjust.crop.ratio')} action={<AdjustTextButton label={t('adjust.crop.reset')} disabled={disabled || !isChanged} onClick={() => apply({ type: 'resetCrop' })} />} />

      <AdjustChipGroup ariaLabel={t('adjust.crop.ratio')}>
        {CROP_ASPECTS.map(aspect => (
          <AdjustChip
            key={aspect.id}
            label={NAMED_RATIOS.has(aspect.id) ? t(`adjust.crop.ratios.${aspect.id}`) : ratioLabel(aspect.id, crop.portrait)}
            selected={crop.aspect === aspect.id}
            disabled={disabled}
            onClick={() => apply({ type: 'setCrop', crop: { aspect: aspect.id } })}
          />
        ))}
      </AdjustChipGroup>

      <LabSegmented
        ariaLabel={t('adjust.crop.orientation')}
        disabled={disabled || !isOrientable}
        value={crop.portrait ? 'portrait' : 'landscape'}
        onChange={(value) => apply({ type: 'setCrop', crop: { portrait: value === 'portrait' } })}
        options={[
          { value: 'landscape', label: t('adjust.crop.landscape'), icon: <CropLandscapeOutlinedIcon /> },
          { value: 'portrait', label: t('adjust.crop.portrait'), icon: <CropPortraitOutlinedIcon /> },
        ]}
      />

      <AdjustGroupHeader title={t('adjust.crop.rotate_and_flip')} />

      <AdjustSlider
        label={t('adjust.crop.straighten')}
        value={crop.straighten}
        valueLabel={`${crop.straighten.toFixed(1)}°`}
        min={-STRAIGHTEN_LIMIT}
        max={STRAIGHTEN_LIMIT}
        step={0.5}
        origin={0}
        disabled={disabled}
        onChange={(straighten) => rotate({ straighten }, 'crop:straighten')}
        onCommit={commit}
        onReset={() => rotate({ straighten: 0 })}
      />

      {/* Two rows of two: the labels fit the 320 px One tool panel as well as the Combine tools list */}
      <AdjustIconActionGroup columns={2}>

        <AdjustIconAction icon={<RotateLeftIcon />} label={t('adjust.crop.rotate_left')} disabled={disabled} onClick={() => rotate({ quarterTurns: crop.quarterTurns - 1 })} />

        <AdjustIconAction icon={<RotateRightIcon />} label={t('adjust.crop.rotate_right')} disabled={disabled} onClick={() => rotate({ quarterTurns: crop.quarterTurns + 1 })} />

        <AdjustIconAction icon={<FlipIcon />} label={t('adjust.crop.flip_horizontal')} selected={crop.flipX} disabled={disabled} onClick={() => apply({ type: 'setCrop', crop: { flipX: !crop.flipX } })} />

        <AdjustIconAction icon={<FlipIcon sx={{ transform: 'rotate(90deg)' }} />} label={t('adjust.crop.flip_vertical')} selected={crop.flipY} disabled={disabled} onClick={() => apply({ type: 'setCrop', crop: { flipY: !crop.flipY } })} />

      </AdjustIconActionGroup>

      {frame && <AdjustReadout label={t('adjust.crop.size')} value={t('adjust.size_px', { width: Math.round(frame.width), height: Math.round(frame.height) })} />}

    </Stack>
  )
}
